import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Nav from "../navbar/nav";
import { trackPageVisit } from "../../utils/pageTracking";

const AppRoute = ({
  component: Component,
  routeKey,
  blankLayout,
  ...props
}) => {
  const location = useLocation();

  useEffect(() => {
    const isBlank = blankLayout ? true : false;
  }, [blankLayout]);

  // Centralized page tracking for all protected routes (excluding administration pages)
  useEffect(() => {
    // Skip tracking for administration pages
    const isAdminPage = location.pathname.includes('/adminPanel/');
    
    // Clear Portfolio List tracking data on app start (fresh session)
    if (!sessionStorage.getItem('portfolioListTrackingInitialized')) {
      const portfolioListPath = '/JumpFive/Portfolio/PortfolioBuilder/PortfolioList';
      sessionStorage.removeItem(`lastTrackedUrl_${portfolioListPath}`);
      sessionStorage.setItem('portfolioListTrackingInitialized', 'true');
    }
    
    if (!isAdminPage) {
      // Check if this navigation is from drawer menu Instant Report (skip tracking)
      const skipDrawerMenuTracking = sessionStorage.getItem('skipDrawerMenuTracking') === 'true';
      if (skipDrawerMenuTracking) {
        sessionStorage.removeItem('skipDrawerMenuTracking');
        return;
      }
      
      // Check if this is a valuation page
      const isValuationPage = location.pathname.includes('/Valuations/ValuationEdit/');
      const hasHash = location.hash && location.hash !== '';
      
      // Check if this is PortfolioEdit page (handled manually in component)
      const isPortfolioEditPage = location.pathname.includes('/PortfolioEdit');
      
      // Check if this is Portfolio List page (handled manually in component)
      const isPortfolioListPage = location.pathname.includes('/PortfolioList');
      
      
      // Check if this is Impact Reports flow (home → ValuationEdit/{id} → summary)
      // Skip tracking for the intermediate ValuationEdit page without hash
      const isImpactReportsFlow = sessionStorage.getItem('isImpactReportsFlow') === 'true';
      
      // Handle PortfolioEdit pages - track only once on initial load
      if (isPortfolioEditPage) {
        // Check if we've already tracked this PortfolioEdit page
        const hasTrackedPortfolioEdit = sessionStorage.getItem('hasTrackedPortfolioEdit') === 'true';
        
        if (!hasTrackedPortfolioEdit) {
          // Mark as tracked and track the page
          sessionStorage.setItem('hasTrackedPortfolioEdit', 'true');
          trackPageVisit(window.location.href);
        }
        return;
      } else {
        // Clear PortfolioEdit tracking flag when navigating away
        sessionStorage.removeItem('hasTrackedPortfolioEdit');
      }
      
      // Handle Portfolio List pages - track on each visit but not on filter updates
      if (isPortfolioListPage) {
        const portfolioListPath = location.pathname;
        const currentUrl = window.location.href;
        
        // Get the last tracked URL for this path
        const lastTrackedUrl = sessionStorage.getItem(`lastTrackedUrl_${portfolioListPath}`);
        
        // Check if this is a filter update (same pathname, different parameters)
        const isFilterUpdate = lastTrackedUrl && 
          lastTrackedUrl.includes(portfolioListPath) && 
          lastTrackedUrl !== currentUrl;
        
        if (!isFilterUpdate) {
          // This is a new visit or return to Portfolio List
          sessionStorage.setItem(`lastTrackedUrl_${portfolioListPath}`, currentUrl);
          
          // Track the page with FULL URL including parameters
          trackPageVisit(window.location.href);
        }
        return;
      } else {
        // Clear the last tracked URL when navigating away from Portfolio List to ANY other page
        const portfolioListPath = '/JumpFive/Portfolio/PortfolioBuilder/PortfolioList';
        sessionStorage.removeItem(`lastTrackedUrl_${portfolioListPath}`);
      }
      
      // For valuation pages, only track if there's no hash (let hash change handle hash changes)
      // For other pages, track normally
      if (isValuationPage && !hasHash) {
        // Skip tracking for Impact Reports intermediate page
        if (isImpactReportsFlow) {
          // Don't clear the flag yet - let hash change handler clear it
          return;
        }
        
        // Skip tracking if hash is about to be set (from valuation directory click)
        const isAboutToSetHash = sessionStorage.getItem('isAboutToSetHash') === 'true';
        if (isAboutToSetHash) {
          // Clear the flag and skip tracking - let hash change handle it
          sessionStorage.removeItem('isAboutToSetHash');
          return;
        }
        
        // Track valuation page without hash (initial load)
        trackPageVisit(window.location.href);
      } else if (!isValuationPage) {
        // Track all non-valuation pages normally
        trackPageVisit(window.location.href);
      }
    }
  }, [location]);

  // Handle hash changes for valuation steps (radius, financial, performance, etc.)
  useEffect(() => {
    const handleHashChange = () => {
      // Skip tracking for administration pages
      const isAdminPage = window.location.pathname.includes('/adminPanel/');
      
      if (!isAdminPage) {
        // Check if this is a valuation page with hash
        const isValuationPage = window.location.pathname.includes('/Valuations/ValuationEdit/');
        const hasHash = window.location.hash && window.location.hash !== '';
        
        // Only track hash changes for valuation pages (step navigation)
        if (isValuationPage && hasHash) {
          // Skip tracking for Impact Reports flow until we reach summary
          const isImpactReportsFlow = sessionStorage.getItem('isImpactReportsFlow') === 'true';
          
          if (isImpactReportsFlow && window.location.hash !== '#summary') {
            return; // Skip tracking intermediate steps in Impact Reports flow
          }
          
          // Clear Impact Reports flag when we reach summary
          if (isImpactReportsFlow && window.location.hash === '#summary') {
            sessionStorage.removeItem('isImpactReportsFlow');
          }
          
          // Hash has already changed at this point
          trackPageVisit(window.location.href);
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []); // Remove location dependency to prevent re-registration

  return (
    <>
      {routeKey !== "aiAnalytics" && <Nav routeKey={routeKey} />}
      <Component {...props} />
    </>
  );
};

export default AppRoute;
